import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import { cn } from '@/app/components/ui/utils';
import { useAsset, useAssets } from '@/hooks/use-assets';
import type { Asset } from '@/lib/types';

interface AssetPickerProps {
  value?: string | number | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  status?: string;
  lengthMin?: string;
  lengthMax?: string;
  allowNone?: boolean;
  noneLabel?: string;
  noneValue?: string;
  className?: string;
}

export function AssetPicker({
  value,
  onChange,
  placeholder = 'Select asset...',
  disabled = false,
  status,
  lengthMin,
  lengthMax,
  allowNone = false,
  noneLabel = 'None',
  noneValue = '',
  className,
}: AssetPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const valueString = value ? String(value) : '';

  const { data: assetsData, isLoading } = useAssets({
    q: debouncedQuery,
    page_size: 50,
    status,
    length_min: lengthMin,
    length_max: lengthMax,
  });

  const selectedAssetId = valueString ? Number(valueString) : 0;
  const { data: fetchedSelectedAsset } = useAsset(selectedAssetId);

  const assets = assetsData?.results ?? [];

  const displayAsset: Asset | null = useMemo(() => {
    if (!valueString) return null;
    return (
      assets.find((asset) => String(asset.id) === valueString) ||
      fetchedSelectedAsset ||
      null
    );
  }, [assets, fetchedSelectedAsset, valueString]);

  const handleSelect = (assetId: string) => {
    onChange(assetId);
    setOpen(false);
  };

  const displayLabel = useMemo(() => {
    if (!valueString) return placeholder;
    if (allowNone && valueString === noneValue) return noneLabel;
    if (displayAsset) {
      return `${displayAsset.serial_number} — ${displayAsset.manufacturer || ''} ${displayAsset.model || ''}`.trim();
    }
    return placeholder;
  }, [valueString, noneValue, noneLabel, allowNone, displayAsset, placeholder]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn('w-full justify-between font-normal', className)}
        >
          {isLoading && !displayAsset ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </span>
          ) : (
            <span className="truncate">{displayLabel}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by serial, model, or manufacturer..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading assets...' : 'No assets found.'}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value={noneValue}
                  onSelect={() => handleSelect(noneValue)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      valueString === noneValue ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {noneLabel}
                </CommandItem>
              )}
              {assets.map((asset) => (
                <CommandItem
                  key={asset.id}
                  value={String(asset.id)}
                  onSelect={() => handleSelect(String(asset.id))}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      valueString === String(asset.id)
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {asset.serial_number} — {asset.manufacturer} {asset.model}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {asset.display_name || asset.model}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
